import { configureStore } from '@reduxjs/toolkit';
import { clone } from 'ramda';
import posthog from 'posthog-js';
import { toast } from 'react-toastify';
import subscribeActionMiddleware, {
  subscribeBefore,
} from 'redux-subscribe-action';
import { createMiddleware } from 'redux-beacon';
import GoogleTagManager from '@redux-beacon/google-tag-manager';

import user, { usersInitialState, logout, setAuthToken } from 'slices/user';
import {
  onUnauthorized,
  onTokenRefreshed,
  authTokenSelector,
} from 'api/request';
import org from 'slices/org';
import treasury from 'slices/treasury';
import payments from 'slices/payments';
import paymentGroups from 'slices/paymentGroups';
import orgFees from 'slices/orgFees';
import contracts from 'slices/contracts';
import misc from 'slices/misc';
import expiringLink from 'slices/expiringLink';
import paymentGroupFiles from 'slices/paymentGroupFiles';
import invoices from 'slices/invoices';
import customers from 'slices/customers';
import plaid from 'slices/plaid';
import charges from 'slices/charges';
import paypal from 'slices/paypal';
import { loadState, saveState } from '../util/localStorage';
import orgApiKeys from 'slices/orgApiKeys';
import wallets from 'slices/wallets';
import notifications from 'slices/notifications';
import orgOauth from 'slices/orgOauth';
import qb from 'slices/qb';
import rapyd from 'slices/rapyd';
import contractTemplates from 'slices/contractTemplates';
import creators from 'euSlices/creators';
import deliverables from 'slices/deliverables';
import agentsMngrs from 'slices/agentsMngrs';
import taxes from 'slices/taxes';
import collectedFees from 'slices/collectedFees';
import tags from 'slices/tags';
import { getIsProd } from 'util/env';

const gtm = GoogleTagManager();

const event = (action, prevState, nextState) => {
  return {
    event: action.type,
  };
};

const eventsMap = {
  '*': event,
};

const gtmMiddleware = createMiddleware(eventsMap, gtm);

let extraMiddleware = [subscribeActionMiddleware];

const isProd = getIsProd();
if (isProd) extraMiddleware = extraMiddleware.concat(gtmMiddleware);

const persistedState = loadState();
const store = configureStore({
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(extraMiddleware),
  reducer: {
    agentsMngrs,
    charges,
    collectedFees,
    contracts,
    contractTemplates,
    creators,
    customers,
    deliverables,
    expiringLink,
    invoices,
    misc,
    notifications,
    org,
    orgApiKeys,
    orgFees,
    orgOauth,
    paymentGroupFiles,
    paymentGroups,
    payments,
    paypal,
    plaid,
    qb,
    rapyd,
    tags,
    taxes,
    treasury,
    user,
    wallets,
  },
  preloadedState: persistedState,
});

// A login lasts 15 minutes; api/request gets the next one from the refresh
// session and hands it here, so the user never notices. A refresh that was
// started for an older login (signed out, or signed in as someone else since)
// must not replace the current one.
onTokenRefreshed((token, staleToken) => {
  if (authTokenSelector(store.getState()) !== staleToken) return;
  store.dispatch(setAuthToken(token));
});

// When no refresh is possible (session revoked, expired, or logged out
// elsewhere) the user goes back to sign in (ProtectedRoute redirects once the
// token is gone) instead of leaving every request failing.
onUnauthorized((sentToken) => {
  // A late 401 for an older login (already signed out, or signed in again
  // since) must not end the current session.
  if (authTokenSelector(store.getState()) !== sentToken) return;
  store.dispatch(logout());
  toast.info('Your session has expired. Please sign in again.', {
    toastId: 'session-expired',
  });
});

store.subscribe(() => {
  const state = store.getState();
  const _user = clone(state.user);
  const _org = clone(state.org);
  const _misc = clone(state.misc);

  saveState({
    user: {
      ...usersInitialState,
      currentUser: _user.currentUser,
    },
    org: _org,
    misc: _misc,
  });
});

posthog.init('phc_Hp01V7m05yHf8OH8tnbYOCQ7T5TD8hTQAiSXOjOzqSP', {
  api_host: 'https://app.posthog.com',
  autocapture: false,
  capture_pageview: false,
});
if (isProd) subscribeBefore((action) => posthog.capture(action.type));

export { store };
